import { useEffect, useRef } from "react";
import { useDispatchApp, useSelectorApp } from "../lib/redux";
import { useLocation } from "react-router-dom";
import i18next from "i18next";
import { getLanguageFormatted } from "../utils/string";
import { isFormPath } from "../router/formRoutes";
import {
  startGetIsAppSumo,
  startGetSubscriptionPlan,
} from "../actions/payments";
import {
  DEFAULT_NAME_BUSINESS,
  IS_WHITE_LABEL_BRAND,
} from "../utils/constants";

const TIDIO_KEY = "cxjvmjdypcas0frum7h09qcxzqg3gk0j";
const TIDIO_BRAND_TOKEN = DEFAULT_NAME_BUSINESS;

let tidioBrandOverride = "";
let tidioLogoOverride = "";
let hasPlayedTidioSidebarAnimation = false;

const onTidioSidebarAnimationStart = () => {
  hasPlayedTidioSidebarAnimation = true;
};

// Routes where Tidio chat should be hidden
const HIDDEN_ROUTES = [
  "/dashboard",
  "/promotional-codes",
  "/global-notifications",
];

const ensureSideContentGlowAnimation = ({ shadowRoot }) => {
  if (shadowRoot.getElementById("tidio-glow-styles")) {
    return;
  }

  const style = document.createElement("style");
  style.id = "tidio-glow-styles";
  style.textContent = `
    @keyframes sidebarSweep {
      0% {
        transform: translateX(-220%) skewX(-18deg);
      }
      100% {
        transform: translateX(320%) skewX(-18deg);
      }
    }

    /*
     * Heartbeat pulse: no transform on .sidebar-highlight (Tidio rotates the tab;
     * parent scale composes badly). Outer box-shadow is clipped by overflow:hidden,
     * so we use inset glow + filter for a strong, visible pulse.
     */
    @keyframes sidebarHeartbeat {
      0%,
      48%,
      100% {
        filter: brightness(1) saturate(1);
        box-shadow: inset 0 0 0 0 rgba(255, 255, 255, 0);
      }
      12% {
        filter: brightness(1.202) saturate(1.13);
        box-shadow:
          inset 0 0 0 2px rgba(255, 255, 255, 0.468),
          inset 0 0 34px 10px rgba(255, 255, 255, 0.252),
          inset 0 0 52px 17px rgba(96, 165, 250, 0.202);
      }
      24% {
        filter: brightness(1) saturate(1);
        box-shadow: inset 0 0 0 0 rgba(255, 255, 255, 0);
      }
      36% {
        filter: brightness(1.13) saturate(1.086);
        box-shadow:
          inset 0 0 0 1px rgba(255, 255, 255, 0.324),
          inset 0 0 23px 7px rgba(255, 255, 255, 0.158),
          inset 0 0 38px 12px rgba(147, 197, 253, 0.158);
      }
    }

    /*
     * Up/down bounce via the separate \`translate\` property so it composes with
     * Tidio's transform: rotate on the sidebar tab (does not wipe the rotation).
     */
    @keyframes sidebarBounce {
      0%,
      100% {
        translate: 0 0;
      }
      50% {
        translate: 0 -10px;
      }
    }

    .sidebar-highlight {
      position: relative;
      overflow: hidden;
      isolation: isolate;
    }

    .sidebar-highlight.sidebar-highlight-animated-once {
      /* delay then: duration, easing, delay, iteration-count */
      animation:
        sidebarHeartbeat 2.1s ease-in-out 1s 4,
        sidebarBounce 1.05s ease-in-out 1s 8;
    }
  `;
  shadowRoot.appendChild(style);
};

// Function to apply custom styles to messages
const applyCustomStyles = () => {
  try {
    // Access Shadow DOM of Tidio chat
    const tidioChat = document.getElementById("tidio-chat");
    if (tidioChat && tidioChat.shadowRoot) {
      const shadowRoot = tidioChat.shadowRoot;
      ensureSideContentGlowAnimation({ shadowRoot });

      const sideContent = shadowRoot.querySelector(".sidebar-content");
      if (sideContent) {
        sideContent.classList.add("sidebar-highlight");
        if (!hasPlayedTidioSidebarAnimation) {
          sideContent.classList.add("sidebar-highlight-animated-once");
          if (!sideContent.dataset.sidebarAnimationListenerAttached) {
            sideContent.addEventListener(
              "animationstart",
              onTidioSidebarAnimationStart,
              {
                once: true,
              }
            );
            sideContent.dataset.sidebarAnimationListenerAttached = "true";
          }
        } else {
          sideContent.classList.remove("sidebar-highlight-animated-once");
        }
        sideContent.style.borderBottomRightRadius = "8px";
        sideContent.style.borderBottomLeftRadius = "8px";
        sideContent.style.borderTopRightRadius = "0px";
        sideContent.style.borderTopLeftRadius = "0px";
      }

      // Remove only the open button (not the close button)
      const openButton = shadowRoot.querySelector(
        'button#button-body[data-testid="widgetButtonBody"]'
      );
      if (
        openButton &&
        openButton?.parentElement?.classList?.contains("chat-closed")
      ) {
        openButton.remove();
      }

      // Apply styles to visitor messages inside Shadow DOM
      const visitorMessages = shadowRoot.querySelectorAll(".message-visitor");
      visitorMessages.forEach((el) => {
        if (el && el.style) {
          el.style.background = "#f5f5f5";
          el.style.color = "#000";
        }
      });

      // Apply styles to operator messages inside Shadow DOM
      const operatorMessages = shadowRoot.querySelectorAll(".message-operator");
      operatorMessages.forEach((el) => {
        if (el && el.style) {
          el.style.background =
            "linear-gradient(to bottom right, #374151, #1f2937, #111827)";
          el.style.color = "#fff";
        }
      });

      replaceTidioBrandText(shadowRoot);
      replaceTidioHeaderAvatar(shadowRoot);
    }
  } catch (error) {
    // Silently fail
  }
};

const replaceBrandToken = (value, token, replacement) => {
  if (!value || !replacement || !value.includes(token)) {
    return value;
  }

  if (!replacement.includes(token)) {
    return value.split(token).join(replacement);
  }

  const placeholder = "\u0000";
  const protectedValue = value.split(replacement).join(placeholder);
  return protectedValue
    .split(token)
    .join(replacement)
    .split(placeholder)
    .join(replacement);
};

const applyTidioAvatarLogo = (element, logoUrl) => {
  if (element.tagName === "IMG") {
    if (element.getAttribute("src") !== logoUrl) {
      element.src = logoUrl;
      element.removeAttribute("srcset");
    }
    element.style.objectFit = "contain";
    return;
  }

  const image = element.querySelector("img");
  if (image) {
    applyTidioAvatarLogo(image, logoUrl);
    return;
  }

  const backgroundImage = element.style.backgroundImage || "";
  if (!backgroundImage.includes(logoUrl)) {
    element.style.backgroundImage = `url("${logoUrl}")`;
    element.style.backgroundSize = "contain";
    element.style.backgroundPosition = "center";
    element.style.backgroundRepeat = "no-repeat";
  }
};

const replaceTidioHeaderAvatar = (root) => {
  const logoUrl = tidioLogoOverride.trim();
  if (!root || !logoUrl) {
    return;
  }

  root.querySelectorAll('[class*="header-ava"]').forEach((element) => {
    applyTidioAvatarLogo(element, logoUrl);
  });
};

const replaceTidioBrandText = (root) => {
  const replacement = tidioBrandOverride.trim();
  if (!root || !replacement || replacement === TIDIO_BRAND_TOKEN) {
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((node) => {
    const parentTag = node.parentElement?.tagName;
    if (parentTag === "STYLE" || parentTag === "SCRIPT") {
      return;
    }

    const nextValue = replaceBrandToken(
      node.nodeValue,
      TIDIO_BRAND_TOKEN,
      replacement
    );
    if (nextValue !== node.nodeValue) {
      node.nodeValue = nextValue;
    }
  });
};

// Apply initial styles
const applyInitialStyles = () => {
  const tidioChat = document.getElementById("tidio-chat");
  if (tidioChat && tidioChat.shadowRoot) {
    tidioChat.style.height = "89vh";
    const shadowRoot = tidioChat.shadowRoot;
    // Only remove the open button (not the close button)
    const openButton = shadowRoot.querySelector(
      'button#button-body[data-testid="widgetButtonBody"]'
    );
    if (
      openButton &&
      openButton?.parentElement?.classList?.contains("chat-closed")
    ) {
      openButton.remove();
    }
    applyCustomStyles();
    return true;
  }
  return false;
};

const useTidio = () => {
  const user = useSelectorApp((state) => state.auth.user);
  const nameBusiness = useSelectorApp((state) => state.ui.theme?.nameBusiness);
  const favicon = useSelectorApp((state) => state.ui.theme?.favicon);
  const dispatch = useDispatchApp();
  tidioBrandOverride = IS_WHITE_LABEL_BRAND && nameBusiness ? nameBusiness : "";
  tidioLogoOverride = IS_WHITE_LABEL_BRAND && favicon ? favicon : "";
  const isAnonymous = user?.isAnonymous;
  const location = useLocation();
  const prevUserIdRef = useRef(null);
  const prevAdvertiserIdRef = useRef(null);
  const observerRef = useRef(null);

  // Check if current route should hide the chat
  const shouldHideChat =
    isFormPath(location.pathname) ||
    HIDDEN_ROUTES.some((route) => location.pathname.startsWith(route)) ||
    isAnonymous;

  // Setup MutationObserver to watch for new messages and re-apply styles
  const setupMessageObserver = () => {
    const tidioChat = document.getElementById("tidio-chat");
    if (!tidioChat || !tidioChat.shadowRoot) return;

    // Disconnect previous observer if exists
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const shadowRoot = tidioChat.shadowRoot;

    // Create observer to watch for DOM changes (new messages)
    observerRef.current = new MutationObserver(() => {
      // Re-apply message styles whenever DOM changes
      applyCustomStyles();
    });
    // Observe the shadow root for changes
    observerRef.current.observe(shadowRoot, {
      childList: true,
      subtree: true,
    });
  };

  const getAsyncProperties = async () => {
    const responseSubscriptionPlan = await dispatch(startGetSubscriptionPlan());
    const subscriptionPlan = responseSubscriptionPlan
      ? responseSubscriptionPlan
      : "none";
    const isAppSumo = await dispatch(startGetIsAppSumo());
    return { subscriptionPlan, isAppSumo: isAppSumo ? "true" : "false" };
  };

  useEffect(() => {
    // 1. Carga inicial del script (solo si no existe)
    if (!document.querySelector(`script[src*="code.tidio.co"]`)) {
      const script = document.createElement("script");
      script.src = `//code.tidio.co/${TIDIO_KEY}.js`;
      script.async = true;
      document.body.appendChild(script);
    }

    const onTidioAction = async () => {
      if (!window.tidioChatApi) {
        return;
      }

      if (i18next.language) {
        document.tidioChatLang = getLanguageFormatted(i18next.language);
      }

      // Case: No user (logout or session not started) OR route should hide chat
      if (!user || shouldHideChat) {
        window.tidioChatApi.hide(); //  Hide the button
        window.tidioChatApi.close(); // Close the window if it was open

        if (!user) {
          prevUserIdRef.current = null;
          prevAdvertiserIdRef.current = null;
        }

        // Optional: If you want to completely remove the DOM trace
        const iframe = document.getElementById("tidio-chat-iframe");
        if (iframe) iframe.style.display = "none";

        // Disconnect observer when hiding
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
        return;
      }

      // --- CASE: USER LOGGED IN AND ROUTE ALLOWS CHAT ---

      // If the user ID changed, we treat it as a new "distinct_id"
      const userChanged = prevUserIdRef.current !== user.id;
      const advertiserChanged =
        prevAdvertiserIdRef.current !== user.advertiserID;

      if (userChanged) {
        window.tidioChatApi.setVisitorData({
          distinct_id: user.uid, // Unique ID from your DB
          email: user.email,
          name: user.name,
        });
        prevUserIdRef.current = user.id;
      }

      if (userChanged || advertiserChanged) {
        const { subscriptionPlan, isAppSumo } = await getAsyncProperties();
        window.tidioChatApi.setContactProperties({
          userID: user.uid,
          agencyID: user.agencyID,
          advertiserID: user.advertiserID,
          plan: subscriptionPlan,
          isAppSumo: isAppSumo,
        });
        prevAdvertiserIdRef.current = user.advertiserID;
      }

      window.tidioChatApi.show(); // Show the widget

      // Listen for chat open event to re-apply styles
      const onChatOpen = () => {
        // Small delay to ensure messages are rendered
        setTimeout(() => {
          applyCustomStyles();
          setupMessageObserver();
        }, 100);
      };

      // Register event listeners for chat open
      window.tidioChatApi.on("open", onChatOpen);

      applyInitialStyles();
      // Try multiple times with delays until elements are ready
      let attempts = 0;
      const maxAttempts = 10;
      const tryApplyStyles = () => {
        attempts++;
        if (applyInitialStyles() || attempts >= maxAttempts) {
          setupMessageObserver();
          return;
        }
        setTimeout(tryApplyStyles, 10);
      };
      tryApplyStyles();
    };

    void onTidioAction();

    return () => {
      // Cleanup observer on unmount
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user,
    shouldHideChat,
    location.pathname,
    nameBusiness,
    favicon,
    window?.tidioChatApi,
  ]); // Triggers when user, route, brand name, or favicon changes
};

/** Opens the Tidio widget (e.g. from NavBar). Uses show so it works after hide() on some routes. */
export const openTidioChat = () => {
  const run = () => {
    if (typeof window === "undefined" || !window.tidioChatApi) {
      return false;
    }
    window.tidioChatApi.show();
    window.tidioChatApi.open();
    return true;
  };

  if (run()) {
    return;
  }

  const onTidioReady = () => {
    run();
  };

  document.addEventListener("tidioChat-ready", onTidioReady, { once: true });
};

export default useTidio;
